import React, { useState, useEffect, useRef } from "react";
import {
  Checkbox,
  Row,
  Col,
  InputNumber,
  Form,
  Spin,
  message,
  Card,
  Space,
  Divider,
} from "antd";
import { useQuery } from "react-query";

import { Locale, ApiManager } from "../../../utils";
import { useSelector } from "react-redux";

import "./albumCommissionForm.scss";
import { getUserInfo } from "../../../utils/redux/reducers/User";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();

function AlbumCommissionForm({
  setCommissions,
  commissions,
  form,
  setSelectedLevels: setLevels,
}) {
  const [selectedLevels, setSelectedLevels] = useState([]);
  const user = useSelector((state) => getUserInfo(state));
  const didInitFromSource = useRef(false);

  const { data: agentLevelsData, isLoading } = useQuery(
    ["agent-levels", user.token],
    async () => {
      if (!user.token) {
        return null;
      }
      const res = await api.agent.list.run({ token: user.token });
      if (res.result) {
        return res.data;
      }
      throw new Error(res.message || "Failed to fetch agent levels");
    },
    {
      enabled: !!user.token,
      onError: (err) => {
        message.error(err.message || "Failed to fetch agent levels");
      },
    }
  );

  const agentLevels = agentLevelsData || [];

  // Seed once from saved commissions; do not re-apply on parent re-renders.
  useEffect(() => {
    if (didInitFromSource.current) return;
    if (!form || !commissions || commissions.length === 0) return;

    didInitFromSource.current = true;
    const levelIds = commissions.map((comm) => {
      form.setFieldsValue({
        [`commissions[${comm.id}][commission]`]: comm.value.commission,
        [`commissions[${comm.id}][parent_commission]`]:
          comm.value.parent_commission,
      });
      return comm.id;
    });
    setSelectedLevels(levelIds);
    if (typeof setLevels === "function") {
      setLevels(levelIds);
    }
  }, [commissions, form, setLevels]);

  const handleLevelChange = (levelId, checked) => {
    if (checked) {
      const nextLevels = [...selectedLevels, levelId];
      setSelectedLevels(nextLevels);
      if (typeof setLevels === "function") {
        setLevels(nextLevels);
      }
      return;
    }

    const nextLevels = selectedLevels.filter((id) => id !== levelId);
    setSelectedLevels(nextLevels);
    if (typeof setLevels === "function") {
      setLevels(nextLevels);
    }
    if (typeof setCommissions === "function") {
      setCommissions(commissions.filter((comm) => comm.id !== levelId));
    }
    form?.setFieldsValue({
      [`commissions[${levelId}][commission]`]: undefined,
      [`commissions[${levelId}][parent_commission]`]: undefined,
    });
  };

  // Get agent level name by ID
  const getAgentLevelName = (agentLevelId) => {
    const level = agentLevels.find((level) => level.id === agentLevelId);
    return level ? i18n.getPropNameByLocale(level, "name") : "";
  };

  // For albums, we don't require album.id like products
  // The commission form can be shown even when creating new album

  return (
    <div className="commission-form-area">
      <Spin spinning={isLoading}>
        <Card title={i18n.t("agentLevel.title")} size="big">
          <div style={{ marginBottom: 16, width: "100%" }}>
            <h4>{i18n.t("agentLevel.selectLevels")}</h4>
            <Row gutter={[16, 8]}>
              {agentLevels.map((level) => (
                <Col key={level.id} xs={24} sm={12} md={12} lg={8}>
                  <Checkbox
                    checked={selectedLevels.includes(level.id)}
                    onChange={(e) =>
                      handleLevelChange(level.id, e.target.checked)
                    }
                  >
                    {i18n.getPropNameByLocale(level, "name")}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </div>

          {selectedLevels.length > 0 && (
            <>
              <Divider />
              <div>
                <h4>{i18n.t("agentLevel.commissionSettings")}</h4>
                {selectedLevels.map((levelId) => (
                  <Row gutter={[16, 16]} key={levelId}>
                    <Card size="medium" title={getAgentLevelName(levelId)}>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item
                          name={`commissions[${levelId}][commission]`}
                          label={i18n.t("agentLevel.commission")}
                          rules={[
                            {
                              required: true,
                              message: i18n.t("agentLevel.commissionWarning"),
                            },
                            {
                              type: "number",
                              min: 0,
                              message: i18n.t(
                                "agentLevel.commissionRangeWarning"
                              ),
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder={i18n.t(
                              "agentLevel.commissionPlaceholder"
                            )}
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                            addonBefore="¥"
                          />
                        </Form.Item>

                        <Form.Item
                          name={`commissions[${levelId}][parent_commission]`}
                          label={i18n.t("agentLevel.parentCommission")}
                          rules={[
                            {
                              required: true,
                              message: i18n.t(
                                "agentLevel.parentCommissionWarning"
                              ),
                            },
                            {
                              type: "number",
                              min: 0,
                              message: i18n.t(
                                "agentLevel.commissionRangeWarning"
                              ),
                            },
                          ]}
                        >
                          <InputNumber
                            placeholder={i18n.t(
                              "agentLevel.parentCommissionPlaceholder"
                            )}
                            min={0}
                            precision={2}
                            style={{ width: "100%" }}
                            addonBefore="¥"
                          />
                        </Form.Item>
                      </Space>
                    </Card>
                  </Row>
                ))}
              </div>
            </>
          )}
        </Card>
      </Spin>
    </div>
  );
}

export default AlbumCommissionForm;

